/**
 * Assistant Store
 * Manages AI assistant chat sessions, messages, and UI context.
 */

import { create } from "zustand";
import { persist } from "zustand/middleware";

export type AssistantRole = "user" | "assistant" | "tool" | "system";

/**
 * Quick-reply chip from the assistant API (`data.chips`).
 * Clicking sends `message` as the next user turn.
 */
export interface AssistantReplyChip {
  label: string;
  message: string;
  action?: string;
}

export interface AssistantMessage {
  id: string;
  role: AssistantRole;
  content: string;
  /**
   * Optional name for tool messages or system tags.
   */
  name?: string;
  /**
   * Opaque metadata returned by the backend (tool calls, tracing ids, etc.).
   */
  metadata?: Record<string, unknown>;
  /**
   * Suggested replies from the last assistant turn; shown only while this
   * message is still the latest in the thread.
   */
  chips?: AssistantReplyChip[];
}

export interface AssistantUiContext {
  /**
   * High-level page identifier, e.g. "profile_story", "wallet", "membership".
   */
  page?: string;
  /**
   * Optional section identifier within the page, e.g. "story", "usp".
   */
  sectionId?: string;
  /**
   * Arbitrary extra context for experiments or analytics.
   */
  extra?: Record<string, unknown>;
}

interface AssistantState {
  messages: AssistantMessage[];
  isSending: boolean;
  error: string | null;
  uiContext: AssistantUiContext | null;
  /**
   * Epoch ms until which the assistant refuses to respond because of
   * repeated misuse (out-of-scope spam, offensive language, etc.).
   * When null or in the past, the assistant is active.
   */
  blockedUntil: number | null;
  /**
   * Count of consecutive misuse events within the current window.
   * Used to decide how strict the tone should be.
   */
  misuseStrikeCount: number;
  /**
   * Backend conversation id. Persisted with messages; cleared on clearSession
   * so the next send starts a new thread.
   */
  sessionId: string | null;

  // Actions
  setMessages: (messages: AssistantMessage[]) => void;
  appendMessage: (message: AssistantMessage) => void;
  updateMessage: (id: string, patch: Partial<AssistantMessage>) => void;
  clearSession: () => void;
  setSessionId: (sessionId: string | null) => void;
  /** Returns existing persisted session id or creates, persists, and returns a new one. */
  getOrCreateSessionId: () => string;
  setIsSending: (isSending: boolean) => void;
  setError: (error: string | null) => void;
  setUiContext: (context: AssistantUiContext | null) => void;
  registerMisuseStrike: (now?: number) => void;
  clearMisuseState: () => void;
}

function generateSessionId(): string {
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
    return crypto.randomUUID();
  }
  return `sess-${Date.now()}-${Math.random().toString(36).slice(2, 11)}`;
}

export const useAssistantStore = create<AssistantState>()(
  persist(
    (set, get) => ({
      messages: [],
      isSending: false,
      error: null,
      uiContext: null,
      blockedUntil: null,
      misuseStrikeCount: 0,
      sessionId: null,

      setMessages: (messages) => set({ messages }),

      appendMessage: (message) =>
        set((state) => ({
          messages: [...state.messages, message],
        })),

      updateMessage: (id, patch) =>
        set((state) => ({
          messages: state.messages.map((msg) =>
            msg.id === id ? { ...msg, ...patch } : msg,
          ),
        })),

      clearSession: () =>
        set({
          messages: [],
          error: null,
          misuseStrikeCount: 0,
          blockedUntil: null,
          sessionId: null,
        }),

      setSessionId: (sessionId) => set({ sessionId }),

      getOrCreateSessionId: () => {
        const existing = get().sessionId;
        if (existing != null && String(existing).trim() !== "") {
          return existing;
        }
        const id = generateSessionId();
        set({ sessionId: id });
        return id;
      },

      setIsSending: (isSending) => set({ isSending }),

      setError: (error) => set({ error }),

      setUiContext: (context) => set({ uiContext: context }),
      registerMisuseStrike: (now) =>
        set((state) => {
          const current = state.misuseStrikeCount ?? 0;
          const next = current + 1;
          // After 3rd strike, block for 5 hours
          const BLOCK_WINDOW_MS = 5 * 60 * 60 * 1000;
          const blockedUntil =
            next >= 3 ? (now ?? Date.now()) + BLOCK_WINDOW_MS : state.blockedUntil;
          return {
            misuseStrikeCount: next,
            blockedUntil,
          };
        }),
      clearMisuseState: () =>
        set({
          misuseStrikeCount: 0,
          blockedUntil: null,
        }),
    }),
    {
      name: "assistant-storage",
      partialize: (state) => ({
        messages: state.messages,
        uiContext: state.uiContext,
        blockedUntil: state.blockedUntil,
        misuseStrikeCount: state.misuseStrikeCount,
        sessionId: state.sessionId,
      }),
      merge: (persisted, current) => {
        const p = persisted as Partial<AssistantState>;
        return {
          ...current,
          ...p,
          sessionId: p.sessionId ?? null,
        };
      },
    },
  ),
);

